
export const COLORTYPES = ['orange', 'yellow', 'green', 'blue', 'purple', 'pink', 'cyan']

const hueRanges = {
  orange: [25, 45],
  yellow: [50, 65],
  green: [100, 150],
  blue: [180, 220],
  purple: [240, 300],
  pink: [330, 350],
  cyan: [160, 180]
};

class ColorGenerator {
  /**
  * Generate low-saturation and clear colors
  * @param {number} count - The number of colors that need to be generated
  * @param {number} saturation - Saturation range (0-100), with low saturation by default
  * @param {number} lightness - Brightness range (0-100), with a default higher brightness to ensure a clear and transparent feeling
  * @returns {Array}
  */
  static generateLowSaturationColors(count = 60, colorType, saturation = [15, 35], lightness = [75, 95]) {
    const colors = [];

    for (let i = 0; i < count; i++) {
      // Evenly distribute the colors on the color wheel
      const hue = colorType ? this.generateHue(colorType) : (i * 360 / count) % 360;
      const sat = Math.floor(Math.random() * (saturation[1] - saturation[0]) + saturation[0]);
      const light = Math.floor(Math.random() * (lightness[1] - lightness[0]) + lightness[0]);

      const hex = this.hslToHex(hue, sat, light);
      colors.push(hex);
    }

    return colors;
  }

  static generateSingleColor(colorType, saturation = 25, lightness = 85) {
    const hue = this.generateHue(colorType);
    return this.hslToHex(hue, saturation, lightness);
  }

  static generateHue(colorType) {
    if (hueRanges[colorType]) {
      const range = hueRanges[colorType] || hueRanges.blue;
      return Math.floor(Math.random() * (range[1] - range[0]) + range[0]);
    } else {
      return Math.floor(Math.random() * 360); // default to blue
    }
  }

  static hslToHex(h, s, l) {
    h /= 360;
    s /= 100;
    l /= 100;

    let r, g, b;

    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;

      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    const toHex = (x) => {
      const hex = Math.round(x * 255).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
  }

  static hexToHsl(hex) {
    hex = hex.replace('#', '');

    const r = parseInt(hex.substr(0, 2), 16) / 255;
    const g = parseInt(hex.substr(2, 2), 16) / 255;
    const b = parseInt(hex.substr(4, 2), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
        default:
          break;
      }
      h /= 6;
    }

    h = Math.round(h * 360);
    s = Math.round(s * 100);
    l = Math.round(l * 100);

    return { h, s, l };
  }

  static getColorHueRange(color) {
    const { h: hue } = this.hexToHsl(color);
    for (const [rangeName, [start, end]] of Object.entries(hueRanges)) {
      if (hue >= start && hue <= end) {
        return rangeName;
      }
    }
  }

  /**
   * Generate soft pastel color schemes
   */
  static generatePastelColors(count = 60, colorType) {
    return this.generateLowSaturationColors(count, colorType, [20, 40], [80, 95]);
  }

  /**
   * Generate watercolor series (clearer and more transparent)
   */
  static generateWatercolorColors(count = 60, colorType) {
    return this.generateLowSaturationColors(count, colorType, [10, 30], [85, 97]);
  }

  /**
   * Generate a misty complexion series (with a hint of gray)
   */
  static generateMistyColors(count = 60, colorType) {
    return this.generateLowSaturationColors(count, colorType, [5, 25], [80, 90]);
  }
}

// const _hueRanges = {};
// for (const colorType in colorPalette) {
//   const hues = colorPalette[colorType].map(color => ColorGenerator.hexToHsl(color).h);
//   _hueRanges[colorType] = [Math.min(...hues), Math.max(...hues)];
// }

// console.log(_hueRanges);

// const colors = {}
// for (const colorType of COLORTYPES) {
//   const color = ColorGenerator.generateLowSaturationColors(10, colorType, [98, 100], [90, 100]);
//   colors[colorType] = color;
// }

// console.log(colors);


// const pastelColors = generatePastelColors(30);
// const watercolorColors = generateWatercolorColors(30);
// const mistyColors = generateMistyColors(30);
// const allPastelColors = generatePastelColors(60);
// const allWaterColors = generateWatercolorColors(60);

// console.log(generateCSSClasses(allPastelColors, 'bg-pastel'));
// console.log(generateCSSClasses(allWaterColors, 'bg-watercolor'));
// console.log(generateCSSClasses(mistyColors, 'bg-misty'))
// console.log(generateCSSClasses(pastelColors, 'bg-pastel'));
// console.log(generateCSSClasses(watercolorColors, 'bg-watercolor'));
export default ColorGenerator;